import { Button, Card, Space, Table, Typography } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { MockBanner } from '../../pages/components/MockBanner';
import { PageHeader } from '../../pages/components/PageHeader';
import { KpiCard } from '../../pages/components/KpiCard';

/** Org screen 10 — consolidated but filterable to one. */
export function OrgFinancePage() {
  return (
    <div>
      <MockBanner when="the finance module + org-level query fanout" />
      <PageHeader
        title="Group finances · September"
        subtitle="Consolidated across 3 institutions · every figure drillable to its source"
        actions={
          <>
            <Button icon={<DownloadOutlined />} disabled>Export for auditor</Button>
            <Button type="primary" disabled>Consolidate budgets</Button>
          </>
        }
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12, marginBottom: 12 }}>
        <KpiCard label="Collected this month" value="₹86.4L" foot="last month: ₹79.2L" tone="ok" />
        <KpiCard label="Outstanding" value="₹1.34Cr" foot="612 students · 63 past 30d" tone="warn" />
        <KpiCard label="Budget spent YTD" value="42%" foot="on track vs 44% expected" />
        <KpiCard label="Late-fee revenue" value="₹64,000" foot="14 institutions across all" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 12 }}>
        <Card size="small" title="Collections vs target · Term 2">
          <div style={{ padding: 12 }}>
            {[
              { name: 'Senior', got: 78, target: 100 },
              { name: 'Primary', got: 82, target: 100 },
              { name: 'College', got: 71, target: 100 },
            ].map((r) => (
              <div key={r.name} style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                  <span>{r.name}</span>
                  <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{r.got}% of ₹target</span>
                </div>
                <div style={{ height: 8, background: '#eeeef1', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ width: `${r.got}%`, height: '100%', background: r.got >= 80 ? '#1c6442' : r.got >= 70 ? '#7d5610' : '#b3382f' }} />
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card size="small" title="Budget utilization · this year">
          <Table
            size="small"
            pagination={false}
            rowKey="head"
            showHeader={false}
            dataSource={BUDGET_HEADS}
            columns={[
              { dataIndex: 'head', key: 'head' },
              { dataIndex: 'used', key: 'used', width: 130, render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{v}</span> },
              { dataIndex: 'pct', key: 'pct', width: 80, align: 'right', render: (v: number) => <span style={{ color: v > 85 ? '#8e2e28' : '#26304a' }}>{v}%</span> },
            ]}
          />
        </Card>
      </div>

      <Card size="small" title="Per institution" style={{ marginTop: 12 }}>
        <Table
          size="small"
          pagination={false}
          rowKey="inst"
          dataSource={INST_ROWS}
          columns={[
            { title: 'Institution', dataIndex: 'inst', key: 'inst', render: (v: string) => <span style={{ fontWeight: 600 }}>{v}</span> },
            { title: 'Target this month', dataIndex: 'target', key: 'target', render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{v}</span> },
            { title: 'Collected', dataIndex: 'collected', key: 'collected', render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{v}</span> },
            { title: '%', dataIndex: 'pct', key: 'pct', width: 80, render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{v}</span> },
            { title: 'Outstanding', dataIndex: 'out', key: 'out', render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{v}</span> },
          ]}
          summary={() => (
            <Table.Summary.Row style={{ background: '#f7f7f5' }}>
              <Table.Summary.Cell index={0}><b>Group total</b></Table.Summary.Cell>
              <Table.Summary.Cell index={1}><b style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>₹1.11Cr</b></Table.Summary.Cell>
              <Table.Summary.Cell index={2}><b style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>₹86.4L</b></Table.Summary.Cell>
              <Table.Summary.Cell index={3}><b style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>77.8%</b></Table.Summary.Cell>
              <Table.Summary.Cell index={4}><b style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>₹1.34Cr</b></Table.Summary.Cell>
            </Table.Summary.Row>
          )}
        />
        <Typography.Text type="secondary" style={{ display: 'block', fontSize: 11.5, marginTop: 8 }}>
          Group total = sum of rows above. A mismatch would mean someone stored a roll-up (D-02 defect).
        </Typography.Text>
      </Card>
    </div>
  );
}

const BUDGET_HEADS = [
  { head: 'Salaries', used: '₹1.44Cr / ₹2.98Cr', pct: 48 },
  { head: 'Utilities', used: '₹18L / ₹42L', pct: 43 },
  { head: 'Repairs & maintenance', used: '₹22L / ₹28L', pct: 79 },
  { head: 'Consumables', used: '₹8.4L / ₹18L', pct: 47 },
  { head: 'Transport', used: '₹27L / ₹35L', pct: 77 },
];

const INST_ROWS = [
  { inst: 'Greenwood Senior', target: '₹52L', collected: '₹40.6L', pct: '78%', out: '₹69L' },
  { inst: 'Greenwood Primary', target: '₹28L', collected: '₹23.0L', pct: '82%', out: '₹18L' },
  { inst: 'Greenwood College', target: '₹31L', collected: '₹22.8L', pct: '71%', out: '₹47L' },
];
